<template>
  <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-flex-shrink-0">
    <!-- Header -->
    <button
      class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-text-left hover:tw-bg-gray-50 tw-rounded-t-xl tw-transition-colors"
      @click="expanded = !expanded"
    >
      <svg class="tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform" :class="expanded ? 'tw-rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
      <h3 class="tw-font-semibold tw-text-xs tw-text-gray-500 tw-tracking-wider tw-uppercase">
        20-daagse analyse
      </h3>
      <span v-if="expanded && isLoading" class="tw-w-3 tw-h-3 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-flex-shrink-0" />
      <template v-else-if="analysis">
        <span class="tw-text-xs tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium" :class="overallBadgeClass">
          {{ analysis.summary.avgRatioToNeighbors ? `${analysis.summary.avgRatioToNeighbors}%` : `${analysis.summary.daysAnalyzed}d` }}
        </span>
        <span v-if="analysis.summary.outlierCount > 0" class="tw-text-xs tw-bg-amber-100 tw-text-amber-700 tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium">
          {{ analysis.summary.outlierCount }} afwijking{{ analysis.summary.outlierCount > 1 ? 'en' : '' }}
        </span>
      </template>
    </button>

    <!-- Content -->
    <div v-if="expanded" class="tw-px-4 tw-pb-4">
      <div v-if="isLoading" class="tw-text-xs tw-text-gray-400 tw-py-6 tw-text-center">
        <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-inline-block tw-mr-2" />
        Data laden...
      </div>

      <div v-else-if="!analysis" class="tw-text-xs tw-text-gray-400 tw-py-4 tw-text-center">
        Onvoldoende data voor analyse.
      </div>

      <template v-else>
        <div class="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-3">

          <!-- ① Prestatie vs buren -->
          <div class="tw-rounded-lg tw-p-2.5" :class="indicatorBg('performance')">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" :class="indicatorIcon('performance')" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="indicatorText('performance')">Prestatie</span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono" :class="indicatorText('performance')">
              {{ analysis.summary.avgRatioToNeighbors ? `${analysis.summary.avgRatioToNeighbors}%` : `${analysis.summary.avgDailyProduction}` }}
            </div>
            <div class="tw-text-xs" :class="indicatorSubtext('performance')">
              {{ analysis.summary.avgRatioToNeighbors ? `van ${label} gem.` : 'Wh/kWp gem./dag' }}
            </div>
          </div>

          <!-- ② Prognose -->
          <div class="tw-rounded-lg tw-p-2.5" :class="indicatorBg('prognosis')">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" :class="indicatorIcon('prognosis')" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="indicatorText('prognosis')">Prognose</span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono" :class="indicatorText('prognosis')">
              {{ analysis.summary.prognosisRatio ? `${analysis.summary.prognosisRatio}%` : '—' }}
            </div>
            <div class="tw-text-xs" :class="indicatorSubtext('prognosis')">
              {{ analysis.summary.prognosisRatio ? 'van maandprognose' : 'geen prognose' }}
            </div>
          </div>

          <!-- ③ Stabiliteit -->
          <div class="tw-rounded-lg tw-p-2.5" :class="indicatorBg('consistency')">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" :class="indicatorIcon('consistency')" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="indicatorText('consistency')">Stabiliteit</span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono" :class="indicatorText('consistency')">
              {{ consistencyLabel }}
            </div>
            <div class="tw-text-xs" :class="indicatorSubtext('consistency')">
              {{ analysis.summary.outlierCount > 0 ? `${analysis.summary.outlierCount} afwijkende dag${analysis.summary.outlierCount > 1 ? 'en' : ''}` : 'geen uitschieters' }}
            </div>
            <!-- Outlier days -->
            <div v-if="outlierSegments.length > 0" class="tw-text-xs tw-mt-1.5 tw-pt-1.5 tw-border-t" :class="indicatorBorder('consistency')">
              <div v-for="(seg, i) in outlierSegments" :key="'outlier-'+i">
                <!-- Multi-day outliers with individual links -->
                <template v-if="seg.outlierDays">
                  {{ seg.text }}
                  <div class="tw-mt-0.5">
                    <span v-for="(od, j) in seg.outlierDays" :key="od.dateStr">
                      <button class="tw-text-blue-600 hover:tw-underline tw-font-medium" @click="$emit('navigate-to-day', od.dateStr)">{{ od.label }}</button>
                      <span v-if="od.ratio !== null" class="tw-text-gray-400"> ({{ od.ratio }}%)</span>
                      <span v-if="j < seg.outlierDays.length - 1" class="tw-text-gray-300"> · </span>
                    </span>
                  </div>
                </template>
                <!-- Single day with link -->
                <template v-else>
                  {{ seg.text.split(':')[0] }}:
                  <button v-if="seg.date" class="tw-text-blue-600 hover:tw-underline tw-font-medium" @click="$emit('navigate-to-day', seg.date)">{{ formatDate(seg.date) }}</button>
                  {{ seg.text.split(')')[0].split('(')[1] ? `(${seg.text.split(')')[0].split('(')[1]})` : '' }}
                </template>
              </div>
            </div>
          </div>

          <!-- ④ Schaduw -->
          <div class="tw-rounded-lg tw-p-2.5" :class="indicatorBg('shadow')">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" :class="indicatorIcon('shadow')" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="indicatorText('shadow')">Schaduw</span>
            </div>
            <template v-if="analysis.summary.shadowInfo">
              <div class="tw-text-sm tw-font-bold" :class="indicatorText('shadow')">
                {{ analysis.summary.shadowInfo.count }}/{{ analysis.summary.shadowInfo.totalDays }} dagen
              </div>
              <div class="tw-text-xs" :class="indicatorSubtext('shadow')">
                vanaf ~{{ analysis.summary.shadowInfo.avgOnsetTime }}, {{ analysis.summary.shadowInfo.avgDropRatio }}% van {{ label }}
              </div>
              <!-- Per-day shadow links -->
              <div v-if="analysis.summary.shadowInfo.days.length <= 5" class="tw-text-xs tw-mt-1.5 tw-pt-1.5 tw-border-t" :class="indicatorBorder('shadow')">
                <span v-for="(sd, i) in analysis.summary.shadowInfo.days" :key="sd.dateStr">
                  <button class="tw-text-blue-600 hover:tw-underline tw-font-medium" @click="$emit('navigate-to-day', sd.dateStr)">{{ formatDate(sd.dateStr).substring(3) }}</button>
                  <span v-if="i < analysis.summary.shadowInfo.days.length - 1" class="tw-text-gray-300"> · </span>
                </span>
              </div>
            </template>
            <template v-else>
              <div class="tw-text-sm tw-font-bold" :class="indicatorText('shadow')">
                {{ shadowLabel }}
              </div>
              <div class="tw-text-xs" :class="indicatorSubtext('shadow')">
                {{ shadowDetail }}
              </div>
            </template>
          </div>

          <!-- ⑤ Communicatie -->
          <div class="tw-rounded-lg tw-p-2.5" :class="indicatorBg('comm')">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" :class="indicatorIcon('comm')" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="indicatorText('comm')">Communicatie</span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono" :class="indicatorText('comm')">
              {{ analysis.summary.daysAnalyzed }}/{{ dateList.length }}
            </div>
            <div class="tw-text-xs" :class="indicatorSubtext('comm')">
              dagen met data
            </div>
            <!-- Missing days detail -->
            <div v-if="commSegment" class="tw-text-xs tw-mt-1.5 tw-pt-1.5 tw-border-t" :class="indicatorBorder('comm')">
              {{ commSegment.text }}
            </div>
          </div>

          <!-- ⑥ Oriëntatie -->
          <div class="tw-rounded-lg tw-p-2.5" :class="analysis.orientationEstimate ? 'tw-bg-blue-50' : 'tw-bg-gray-50'">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" :class="analysis.orientationEstimate ? 'tw-text-blue-500' : 'tw-text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="analysis.orientationEstimate ? 'tw-text-blue-700' : 'tw-text-gray-500'">Oriëntatie</span>
            </div>
            <template v-if="analysis.orientationEstimate">
              <div class="tw-text-sm tw-font-bold tw-text-blue-700">
                ~{{ analysis.orientationEstimate.azimuth }}° {{ analysis.orientationEstimate.azimuthLabel }}
              </div>
              <div class="tw-text-xs tw-text-blue-500">
                <template v-if="analysis.orientationEstimate.tilt !== null">helling ~{{ analysis.orientationEstimate.tilt }}°</template>
              </div>
              <div class="tw-text-xs tw-mt-1.5 tw-pt-1.5 tw-border-t tw-border-blue-100 tw-text-blue-500">
                Geschat o.b.v.
                <button class="tw-text-blue-600 hover:tw-underline tw-font-medium" @click="$emit('navigate-to-day', analysis.orientationEstimate.clearDay)">
                  {{ formatDate(analysis.orientationEstimate.clearDay) }}
                </button>
                ({{ analysis.orientationEstimate.smoothness }}% helder)
              </div>
            </template>
            <template v-else>
              <div class="tw-text-sm tw-font-bold tw-text-gray-500">—</div>
              <div class="tw-text-xs tw-text-gray-400">geen heldere dag in periode</div>
            </template>
          </div>

        </div>

        <!-- ── Day-by-day table ─────────────────────────────────────── -->
        <div>
          <button
            class="tw-text-xs tw-text-gray-400 hover:tw-text-gray-600 tw-flex tw-items-center tw-gap-1 tw-transition-colors"
            @click="showDayTable = !showDayTable"
          >
            <svg class="tw-w-3 tw-h-3 tw-transition-transform" :class="showDayTable ? 'tw-rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
            Dagoverzicht
          </button>
          <div v-if="showDayTable" class="tw-mt-2 tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
            <table class="tw-w-full tw-text-xs">
              <thead>
                <tr class="tw-bg-gray-50 tw-text-gray-500 tw-text-left">
                  <th class="tw-px-2 tw-py-1.5">Dag</th>
                  <th class="tw-px-2 tw-py-1.5 tw-text-right">Wh/kWp</th>
                  <th v-if="hasNeighbors" class="tw-px-2 tw-py-1.5 tw-w-24">vs {{ label }}</th>
                  <th class="tw-px-2 tw-py-1.5 tw-text-right">Piek</th>
                  <th class="tw-px-2 tw-py-1.5 tw-text-right">Uren</th>
                  <th class="tw-px-2 tw-py-1.5 tw-text-right" title="Helderheid van de productiefcurve (hogere score = meer zonneuren zonder bewolking)">Helder</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="day in analysis.dailyStats.filter(d => d.hasData)"
                  :key="day.dateStr"
                  class="tw-border-t tw-border-gray-50 hover:tw-bg-blue-50 tw-cursor-pointer tw-transition-colors"
                  @click="$emit('navigate-to-day', day.dateStr)"
                >
                  <td class="tw-px-2 tw-py-1.5 tw-font-medium tw-text-gray-700">{{ formatDate(day.dateStr) }}</td>
                  <td class="tw-px-2 tw-py-1.5 tw-text-right tw-font-mono tw-text-gray-700">{{ Math.round(day.total) }}</td>
                  <td v-if="hasNeighbors" class="tw-px-2 tw-py-1.5">
                    <div v-if="getDayRatio(day.dateStr) !== null" class="tw-flex tw-items-center tw-gap-1.5">
                      <div class="tw-flex-1 tw-bg-gray-100 tw-rounded-full tw-h-1.5 tw-overflow-hidden">
                        <div
                          class="tw-h-full tw-rounded-full tw-transition-all"
                          :class="ratioBarColor(getDayRatio(day.dateStr))"
                          :style="{ width: Math.min(getDayRatio(day.dateStr), 150) / 1.5 + '%' }"
                        />
                      </div>
                      <span
                        class="tw-font-mono tw-w-8 tw-text-right tw-font-medium"
                        :class="ratioTextColor(getDayRatio(day.dateStr))"
                      >{{ getDayRatio(day.dateStr) }}</span>
                    </div>
                    <span v-else class="tw-text-gray-300">—</span>
                  </td>
                  <td class="tw-px-2 tw-py-1.5 tw-text-right tw-font-mono tw-text-gray-500">{{ Math.round(day.peak) }}</td>
                  <td class="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500">{{ (day.slots / 4).toFixed(1) }}</td>
                  <td class="tw-px-2 tw-py-1.5 tw-text-right tw-font-mono" :class="day.smoothness > 60 ? 'tw-text-blue-500' : day.smoothness > 30 ? 'tw-text-gray-500' : 'tw-text-gray-300'">{{ day.smoothness > 0 ? day.smoothness + '%' : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useQueries } from '@tanstack/vue-query'
import { format, subDays } from 'date-fns'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import { analyzeMultiDay } from './multiDayAnalysis'
import { MAX_NEIGHBORS, ANALYSIS_DAYS_MAIN, ANALYSIS_DAYS_NEIGHBORS } from './constants'
import { STATUS_COLORS, statusForRatio, ratioBarColor, ratioTextColor } from './statusColors'
import { formatDayNL } from './formatters'

const props = defineProps({
  serialNumber: { type: String, required: true },
  // how much of the serial's address the main line covers: address (default, every meter there, a
  // CVZ's fake addresses too), installation or meter; neighbors always cover their own address
  scope: { type: String, default: 'address' },
  neighborMeters: { type: Array, default: () => [] },
  neighborMatchType: { type: String, default: null },
  mainWp: { type: Number, default: null },
  neighborWpMap: { type: Object, default: () => ({}) },
  expectedProduction: { type: Number, default: null },
})

defineEmits(['navigate-to-day'])

const expanded = ref(false)
const showDayTable = ref(false)

const label = computed(() => props.neighborMatchType === 'postcode' ? 'postcode' : 'buren')
const hasNeighbors = computed(() => props.neighborMeters.length > 0)

// Date ranges: 20 days for main meter, 10 days for neighbors
// Exclude today — data is incomplete (retrieval runs ~13:30)
const today = new Date()
const yesterday = subDays(today, 1)
const mainDateFrom = format(subDays(yesterday, ANALYSIS_DAYS_MAIN - 1), 'yyyy-MM-dd')
const neighborDateFrom = format(subDays(yesterday, ANALYSIS_DAYS_NEIGHBORS - 1), 'yyyy-MM-dd')
const dateUntil = format(yesterday, 'yyyy-MM-dd')
const mainDateFromISO = new Date(mainDateFrom).toISOString()
const neighborDateFromISO = new Date(neighborDateFrom).toISOString()
const dateUntilISO = new Date(dateUntil + 'T23:59:59').toISOString()

const dateList = computed(() => {
  const dates = []
  for (let i = ANALYSIS_DAYS_MAIN; i >= 1; i--) dates.push(format(subDays(today, i), 'yyyy-MM-dd'))
  return dates
})

// Fetch main meter (20 days)
const { data: mainData, isLoading: isLoadingMain } = useQuery({
  queryKey: computed(() => ['multiDay', props.serialNumber, props.scope, mainDateFrom]),
  queryFn: async () => {
    const urlParams = new URLSearchParams({ date_from: mainDateFromISO, date_until: dateUntilISO, scope: props.scope })
    const result = await fetchData(`${READINGS_API_BASE}/meters/${props.serialNumber}/quarter-hour-deltas?${urlParams}`)
    return result?.status === 'OK' ? result.data : []
  },
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
  enabled: computed(() => expanded.value),
})

// Fetch neighbors (10 days only — lighter load)
const neighborQueries = computed(() =>
  expanded.value
    ? props.neighborMeters.slice(0, MAX_NEIGHBORS).map(meter => ({
        queryKey: ['multiDay', meter.serialNumber, neighborDateFrom],
        queryFn: async () => {
          const urlParams = new URLSearchParams({ date_from: neighborDateFromISO, date_until: dateUntilISO })
          const result = await fetchData(`${READINGS_API_BASE}/meters/${meter.serialNumber}/quarter-hour-deltas?${urlParams}`)
          return result?.status === 'OK' ? result.data : []
        },
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
      }))
    : []
)

const neighborResults = useQueries({ queries: neighborQueries })

const neighborDataMap = ref({})
watch(neighborResults, (results) => {
  const map = {}
  if (!results) return
  const meters = props.neighborMeters.slice(0, MAX_NEIGHBORS)
  for (let i = 0; i < results.length; i++) {
    if (meters[i] && results[i]?.data) map[meters[i].serialNumber] = results[i].data
  }
  neighborDataMap.value = map
}, { deep: true, immediate: true })

const neighborLoading = ref(false)
watch(neighborResults, (results) => {
  if (!results || results.length === 0) { neighborLoading.value = false; return }
  neighborLoading.value = results.some(q => q?.isLoading || q?.isFetching)
}, { deep: true, immediate: true })

const isLoading = computed(() => expanded.value && (isLoadingMain.value || neighborLoading.value))

// Analysis
const analysis = computed(() => {
  if (!mainData.value || mainData.value.length === 0) return null
  return analyzeMultiDay({
    mainData: mainData.value,
    neighborDataMap: neighborDataMap.value,
    neighbors: props.neighborMeters.slice(0, MAX_NEIGHBORS),
    mainWp: props.mainWp,
    neighborWpMap: props.neighborWpMap,
    matchType: props.neighborMatchType,
    dateList: dateList.value,
    expectedProduction: props.expectedProduction,
  })
})

// ── Segment extractors (for embedding in blocks) ────────────────────────────

const outlierSegments = computed(() =>
  (analysis.value?.segments || []).filter(s =>
    s.text?.includes('opvallend la') || s.text?.includes('Beste dag') || s.text?.includes('Slechtste dag')
  )
)

const commSegment = computed(() =>
  analysis.value?.segments?.find(s => s.text?.includes('communicatie') || s.text?.includes('zonder data'))
)

const shadowSegment = computed(() =>
  analysis.value?.segments?.find(s => s.text?.includes('chaduw') || s.text?.includes('ochtend') || s.text?.includes('middag'))
)

const shadowLabel = computed(() => {
  const s = shadowSegment.value
  if (!s) return 'Geen patroon'
  if (s.text.includes('ochtend') && !s.text.includes('middag')) return 'Ochtend'
  if (s.text.includes('middag') && !s.text.includes('ochtend')) return 'Middag'
  if (s.text.includes('Zowel')) return 'Beide'
  return 'Gedetecteerd'
})

const shadowDetail = computed(() => {
  const s = shadowSegment.value
  if (!s) return 'geen schaduwpatroon'
  if (s.text.includes('ochtend') && !s.text.includes('middag')) return 'lagere ochtendproductie'
  if (s.text.includes('middag') && !s.text.includes('ochtend')) return 'lagere middagproductie'
  return 'ochtend + middag lager'
})

const consistencyLabel = computed(() => {
  const o = analysis.value?.summary?.outlierCount ?? 0
  if (o === 0) return 'Stabiel'
  if (o <= 2) return 'Wisselend'
  return 'Instabiel'
})

// ── Visual helpers ──────────────────────────────────────────────────────────

const overallBadgeClass = computed(() => {
  const r = analysis.value?.summary?.avgRatioToNeighbors
  if (!r) return 'tw-bg-gray-100 tw-text-gray-500'
  if (r < 50) return 'tw-bg-red-100 tw-text-red-700'
  if (r < 75) return 'tw-bg-amber-100 tw-text-amber-700'
  if (r < 90) return 'tw-bg-yellow-100 tw-text-yellow-700'
  return 'tw-bg-green-100 tw-text-green-700'
})

const getDayRatio = (dateStr) => {
  const dr = analysis.value?.dailyRatios?.find(r => r.dateStr === dateStr)
  return dr?.ratio != null ? Math.round(dr.ratio) : null
}

// ratioBarColor and ratioTextColor imported from statusColors.js

const indicatorStatus = (type) => {
  if (!analysis.value) return 'neutral'
  const s = analysis.value.summary
  switch (type) {
    case 'performance': {
      const r = s.avgRatioToNeighbors
      if (!r) return 'neutral'
      return r < 50 ? 'critical' : r < 75 ? 'warning' : r < 90 ? 'moderate' : 'good'
    }
    case 'consistency':
      return s.outlierCount > 2 ? 'warning' : s.outlierCount > 0 ? 'moderate' : 'good'
    case 'shadow':
      return shadowSegment.value ? (shadowSegment.value.type === 'critical' ? 'critical' : 'warning') : 'good'
    case 'comm':
      if (s.daysAnalyzed < dateList.value.length - 3) return 'critical'
      return s.daysAnalyzed < dateList.value.length ? 'moderate' : 'good'
    case 'prognosis': {
      const p = s.prognosisRatio
      if (!p) return 'neutral'
      return p < 50 ? 'critical' : p < 75 ? 'warning' : p < 90 ? 'moderate' : 'good'
    }
    default: return 'neutral'
  }
}

const indicatorBg = (t) => STATUS_COLORS[indicatorStatus(t)]?.bg || STATUS_COLORS.neutral.bg
const indicatorIcon = (t) => STATUS_COLORS[indicatorStatus(t)]?.icon || STATUS_COLORS.neutral.icon
const indicatorText = (t) => STATUS_COLORS[indicatorStatus(t)]?.text || STATUS_COLORS.neutral.text
const indicatorSubtext = (t) => STATUS_COLORS[indicatorStatus(t)]?.sub || STATUS_COLORS.neutral.sub
const indicatorBorder = (t) => STATUS_COLORS[indicatorStatus(t)]?.border || STATUS_COLORS.neutral.border

const formatDate = formatDayNL
</script>
